import {
  Button,
  ButtonGroup,
  Combobox,
  Dropdown,
  Input,
  Spinner,
  Stack
} from "@adfenix/alexandria";
import { Controller, useForm } from "react-hook-form";
import Divider from "components/Divider";
import { GenericError } from "components/GenericError";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";
import { OverviewItem } from "./Item";
import { getProgramOverview, createProgram } from "api/services/programService";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Modal } from "components/Modal";
import { ChangeEvent, useState } from "react";
import { toast } from "react-toastify";
import { useCheckPermission } from "hooks/usePermissions";
import { OfficeConfig } from "types/configServiceModels";
import { getOfficesByHqId } from "api/services/configService";
import { AppMode } from "../details";
import { ProgramService } from "@adfenix/types";
import { SUPPORTED_PROGRAM_SUBJECT_TYPES } from "utils/constants";
import { customerFacingPublishingMode } from "../../../../utils/publishingMode";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-grid-system";
import { BackOfficePage } from "components/Page/BackOfficePage";
import { TFunction } from "i18next";

type Filter = "All programs" | "Included offices" | "Excluded offices";
type ActiveStatus = "All" | "Active" | "Inactive";
type PublishingModes = "All" | ProgramService.PublishingMode;
type SubjectTypes = "All" | ProgramService.SubjectType;
const paidBy = ["All", "Hq", "Office", "Agent", "Seller"];

export const sortOrder = [
  "PropertyUpcoming",
  "PropertyForSale",
  "CampaignEnd",
  "ProgramInstanceCompleted",
  "PropertySold",
  "Manual",
  "Custom"
];

const statusItems = ["All", "Active", "Inactive"] as ActiveStatus[];
const getPublishingModeItems = (t: TFunction) =>
  [
    "All",
    customerFacingPublishingMode("Manual", t),
    customerFacingPublishingMode("MachineAutoPublish", t),
    customerFacingPublishingMode("PremiumAutoPublish", t)
  ] as string[];

const triggers = [
  "All triggers",
  "PropertyForSale",
  "PropertySold",
  "PropertyUpcoming",
  "Manual",
  "Custom"
] as Triggers[];

type Triggers =
  | "All triggers"
  | "PropertyForSale"
  | "PropertySold"
  | "PropertyUpcoming"
  | "Manual"
  | "Custom";

const subjectTypes = [
  "All",
  ...SUPPORTED_PROGRAM_SUBJECT_TYPES
] as SubjectTypes[];

interface Props {
  appMode: AppMode;
}

export const ProgramBuilderOverview = ({ appMode }: Props) => {
  const { t } = useTranslation();

  const { hqId } = useParams<HqIdRouteParams>();
  const navigate = useNavigate();
  const hasEditPermission = useCheckPermission({
    scope: "Program",
    action: "Edit"
  });

  const { data: offices } = useQuery(
    ["getOffices", hqId],
    () => getOfficesByHqId(hqId ?? ""),
    {
      enabled: Boolean(hqId)
    }
  );

  const {
    data,
    isInitialLoading: isLoading,
    error,
    refetch
  } = useQuery(["programOverview", hqId], () => getProgramOverview(hqId), {
    enabled: Boolean(hqId)
  });

  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [subjectType, setSubjectType] =
    useState<ProgramService.SubjectType>("Property");

  const { watch, control } = useForm<{
    exclusionFilter: Filter;
    triggerFilter: Triggers;
    activeStatusFilter: ActiveStatus;
    publishingModeFilter: PublishingModes;
    availableForOfficeFilter: OfficeConfig | undefined;
    subjectTypeFilter: SubjectTypes;
    paidByFilter: string;
  }>({
    defaultValues: {
      exclusionFilter: "All programs",
      triggerFilter: "All triggers",
      activeStatusFilter: "All",
      publishingModeFilter: "All",
      availableForOfficeFilter: undefined,
      subjectTypeFilter: "All",
      paidByFilter: "All"
    }
  });

  const { mutateAsync: triggerCreateProgram, isLoading: isCreatingProgram } =
    useMutation(createProgram, {
      onSuccess: programId => {
        navigate(`/${appMode}/hq/${hqId}/program-builder/${programId}`);
        toast.success(
          t("programBuilder.program.overview.createProgram.toast.success.")
        );
        refetch();
      }
    });

  const handleCloseModal = () => {
    setName("");
    setShowModal(false);
  };

  const filterData = (): ProgramService.ProgramSearchModel[] => {
    if (!data) return [];

    const filteredData = data.documents
      .filter(item => {
        if (!availableForOfficeFilter) return item;
        return (
          (item?.officeMode == "ActiveOnSelectedOffices" &&
            item?.selectedOffices.filter(
              office => office.id == availableForOfficeFilter.id
            ).length > 0) ||
          (item?.officeMode == "ActiveOnAllOfficesExceptSelected" &&
            item?.selectedOffices.filter(
              office => office.id == availableForOfficeFilter.id
            ).length == 0)
        );
      })
      .filter(item => {
        switch (exclusionFilter) {
          case "All programs":
            return item;
          case "Excluded offices":
            return item?.officeMode === "ActiveOnAllOfficesExceptSelected";
          case "Included offices":
            return item?.officeMode === "ActiveOnSelectedOffices";
        }
      })
      .filter(item => {
        switch (triggerFilter) {
          case "All triggers":
            return item;
          case "PropertyForSale":
            return item?.triggerEvent === "PropertyForSale";
          case "PropertySold":
            return item?.triggerEvent === "PropertySold";
          case "PropertyUpcoming":
            return item?.triggerEvent === "PropertyUpcoming";
          case "Manual":
            return item?.triggerEvent === "Manual";
          case "Custom":
            // Custom triggers have a UUID instead of a static triggerEvent.
            return !sortOrder.includes(item.triggerEvent);
        }
      })
      .filter(item => {
        switch (activeStatusFilter) {
          case "All":
            return item;
          case "Active":
            return item?.active;
          case "Inactive":
            return !item?.active;
        }
      })
      .filter(item => {
        switch (subjectTypeFilter) {
          case "All":
            return item;
          case "Property":
            return item?.subjectType === "Property";
          case "Agent":
            return item?.subjectType === "Agent";
          case "Office":
            return item?.subjectType === "Office";
        }
      })
      .filter(item => {
        switch (publishingModeFilter) {
          case "All":
            return item;
          case customerFacingPublishingMode("Manual", t):
            return item?.publishingMode === "Manual";
          case customerFacingPublishingMode("MachineAutoPublish", t):
            return item?.publishingMode === "MachineAutoPublish";
          case customerFacingPublishingMode("PremiumAutoPublish", t):
            return item?.publishingMode === "PremiumAutoPublish";
        }
      })
      .filter(item => {
        switch (paidByFilter) {
          case "All":
            return item;
          default:
            return item?.paidBy.some(x => x === paidByFilter);
        }
      });
    return sortOrder.reduce(
      (acc: ProgramService.ProgramSearchModel[], curr) => {
        acc.push(
          ...filteredData
            .filter(
              item =>
                item.triggerEvent === curr ||
                (curr === "Custom" && !sortOrder.includes(item.triggerEvent))
            )
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        return acc;
      },
      []
    );
  };
  const handleCreateProgram = async () => {
    try {
      if (!hqId) throw "No HQID!";
      await triggerCreateProgram({ hqId, name, subjectType });
    } catch {
      toast.error(
        t("programBuilder.program.overview.createProgram.toast.error")
      );
    } finally {
      setShowModal(false);
    }
  };

  const {
    exclusionFilter,
    triggerFilter,
    activeStatusFilter,
    publishingModeFilter,
    availableForOfficeFilter,
    subjectTypeFilter,
    paidByFilter
  } = watch();

  if (isLoading || isCreatingProgram) return <Spinner centered />;

  if (!data || error) return <GenericError />;

  return (
    <BackOfficePage
      title={t("programBuilder.program.overview.header")}
      hqPicker={appMode === "back-office"}
      action={
        <Button
          logLabel="create-program-button"
          style={{ marginLeft: "12px" }}
          onClick={() => setShowModal(true)}
          variant="secondary"
          disabled={!hasEditPermission}
        >
          {t("programBuilder.program.overview.button.newProgram")}
        </Button>
      }
    >
      <Stack spacing={"xs"}>
        <Row>
          <Col>
            <Controller
              name="activeStatusFilter"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label={t(
                    "programBuilder.program.overview.activeStatus.label"
                  )}
                  items={statusItems}
                  {...field}
                  onChange={value => field.onChange(value)}
                />
              )}
            />
          </Col>
          <Col>
            <Controller
              name="subjectTypeFilter"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label={t("programBuilder.program.overview.subjectType.label")}
                  items={subjectTypes}
                  {...field}
                  onChange={value => field.onChange(value)}
                />
              )}
            />
          </Col>
          <Col>
            <Controller
              name="triggerFilter"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label={t("programBuilder.program.overview.trigger.label")}
                  items={triggers}
                  {...field}
                  onChange={value => field.onChange(value)}
                />
              )}
            />
          </Col>
          <Col>
            <Controller
              name="publishingModeFilter"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label={t(
                    "programBuilder.program.overview.piblishingMode.label"
                  )}
                  items={getPublishingModeItems(t)}
                  {...field}
                  onChange={value => field.onChange(value)}
                />
              )}
            />
          </Col>
          <Col>
            <Controller
              name="paidByFilter"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Paid by"
                  items={paidBy}
                  {...field}
                  onChange={value => field.onChange(value)}
                />
              )}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <Controller
              name="availableForOfficeFilter"
              control={control}
              render={({ field }) => (
                <Combobox
                  items={offices ?? []}
                  valueObjectIndexAccessor="name"
                  isSearchable
                  placeholder={t(
                    "programBuilder.program.overview.availableForOffice.name.placeholder"
                  )}
                  label={t(
                    "programBuilder.program.overview.availableForOffice.name.label"
                  )}
                  isClearable
                  defaultItem={availableForOfficeFilter}
                  onChange={value => field.onChange(value)}
                />
              )}
            />
          </Col>
        </Row>
      </Stack>
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <Modal.Card>
          <Modal.Card.Head onClose={handleCloseModal}>
            <Modal.Card.Title>
              {t("programBuilder.program.overview.modal.title.createProgram")}
            </Modal.Card.Title>
          </Modal.Card.Head>

          <Modal.Card.Body>
            <Stack spacing={"m"}>
              <Input
                label={t("programBuilder.program.overview.modal.name.label")}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value);
                }}
              />

              <Dropdown<ProgramService.SubjectType>
                items={SUPPORTED_PROGRAM_SUBJECT_TYPES}
                label={t("programBuilder.program.overview.modal.subject.label")}
                onChange={val => setSubjectType(val)}
                defaultValue={subjectType}
              />
            </Stack>
          </Modal.Card.Body>

          <Modal.Card.Foot>
            <ButtonGroup>
              <Button
                logLabel="modal-create-program-button"
                disabled={!name}
                onClick={handleCreateProgram}
                variant="secondary"
              >
                {t("programBuilder.program.overview.modal.button.create")}
              </Button>
            </ButtonGroup>
          </Modal.Card.Foot>
        </Modal.Card>
      </Modal>

      <Divider />
      <Stack spacing="m">
        {filterData().map(item => (
          <OverviewItem key={item.id} item={item} appMode={appMode} />
        ))}
      </Stack>
    </BackOfficePage>
  );
};
